/**
 * Service for managing the Founder tier waitlist
 * Provides functions to add users to the waitlist and retrieve waitlist data
 */
import { supabase } from '../lib/supabase';

export const waitlistService = {
  /**
   * Add a user to the Founder tier waitlist
   * @param {string} email - User's email address
   * @returns {Promise<Object>} Response with success status or error
   */
  async joinWaitlist(email) {
    try {
      // Validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Invalid email format');
      }

      // Check if table exists first - if not, we'll gracefully handle it
      try {
        const { data, error } = await supabase
          .from('founder_waitlist')
          .upsert({ email, status: 'pending' }, { onConflict: 'email' })
          .select();

        if (error && error.code === '42P01') {
          // Table doesn't exist yet - store in memory or local storage as fallback
          console.warn('Waitlist table not yet created. Storing in localStorage.');
          this._storeLocalWaitlist(email);
          return {
            success: true,
            message: 'Added to waitlist (will sync when table is ready)',
            email
          };
        }

        if (error) {
          throw new Error(error.message);
        }

        return {
          success: true,
          message: 'Successfully joined the waitlist',
          email
        };
      } catch (dbError) {
        // Fallback to localStorage if DB fails
        console.warn('Database error, using localStorage fallback:', dbError);
        this._storeLocalWaitlist(email);
        return {
          success: true,
          message: 'Added to waitlist (offline mode)',
          email
        };
      }
    } catch (error) {
      throw new Error(error.message || 'Failed to join waitlist');
    }
  },

  /**
   * Check if user is on waitlist
   * @param {string} email - User's email address
   * @returns {Promise<boolean>} True if on waitlist
   */
  async isOnWaitlist(email) {
    try {
      const { data, error } = await supabase
        .from('founder_waitlist')
        .select('id')
        .eq('email', email)
        .single();

      if (error && error.code === '42P01') {
        // Table doesn't exist, check localStorage
        return this._checkLocalWaitlist(email);
      }

      return !error && !!data;
    } catch (error) {
      console.error('Error checking waitlist status:', error);
      return this._checkLocalWaitlist(email);
    }
  },

  /**
   * Get all waitlist entries (admin only)
   * @returns {Promise<Array>} Array of waitlist entries
   */
  async getWaitlist() {
    try {
      const { data, error } = await supabase
        .from('founder_waitlist')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching waitlist:', error);
      return [];
    }
  },

  /**
   * Update waitlist entry status
   * @param {string} email - User's email address
   * @param {string} status - New status (pending, notified, converted)
   * @returns {Promise<Object>} Updated entry
   */
  async updateWaitlistStatus(email, status) {
    try {
      const { data, error } = await supabase
        .from('founder_waitlist')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('email', email)
        .select();

      if (error) {
        throw new Error(error.message);
      }

      return data?.[0] || null;
    } catch (error) {
      console.error('Error updating waitlist status:', error);
      throw error;
    }
  },

  /**
   * Store email in localStorage as fallback
   * @private
   */
  _storeLocalWaitlist(email) {
    try {
      const waitlist = JSON.parse(localStorage.getItem('founder_waitlist') || '[]');
      if (!waitlist.includes(email)) {
        waitlist.push(email);
        localStorage.setItem('founder_waitlist', JSON.stringify(waitlist));
      }
    } catch (error) {
      console.error('Error storing to localStorage:', error);
    }
  },

  /**
   * Check localStorage for email
   * @private
   */
  _checkLocalWaitlist(email) {
    try {
      const waitlist = JSON.parse(localStorage.getItem('founder_waitlist') || '[]');
      return waitlist.includes(email);
    } catch (error) {
      return false;
    }
  }
};
